import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDrop } from "react-dnd";
import { CanvasContext } from "../../../contexts/CanvasContext";
import ButtonComponent from "./ButtonComponent";
import ImageComponent from "./ImageComponent";
import HeaderComponent from "./HeaderComponent";
import ParagraphComponent from "./ParagraphComponent";
import IconComponent from "./IconComponent";
import LinkComponent from "./LinkComponent";
import CheckboxComponent from "./CheckboxComponent";
import ListComponent from "./ListComponent";
import RadioComponent from "./RadioComponent";
import TableComponent from "./TableComponent";
import TextInputComponent from "./TextComponent";
import VideoComponent from "./VideoComponent";
import RangeComponent from "./RangeComponent";
import PasswordInputComponent from "./PasswordInputComponent";
import EmailInputComponent from "./EmailInputComponent";
import NumberInputComponent from "./NumberInputComponent";
import DateInputComponent from "./DateInputComponent";
import ColorInputComponent from "./ColorInputComponent";
import FileInputComponent from "./FileInputComponent";
import AudioComponent from "./AudioComponent";
import DropDownComponent from "./DropDownComponent";
import MapComponent from "./MapComponent";
import Nav from "./Nav";
import HeaderTag from "./HeaderTag";
import Hgroup from "./Hgroup";
import Footer from "./Footer";
import Section from "./Section";
import Span from "./Span";
import FormComponent from "./FormComponent";
import Division from "./Division";
import ArticleComponent from "./ArticleComponent";
import AsideComponent from "./AsideComponent";
import BRTag from "./BRTag";
import HRTag from "./HRTag";
import OutputComponent from "./OutputComponent";
import IframeComponent from "./IframeComponent";

const MainComponent = ({ main }) => {
  const { addDivision, hoveredDivision, addElement, addCanvasDrop, files } =
    useContext(CanvasContext);
  const mainRef = useRef(null);
  const isHovered = main.id === hoveredDivision;
  const [isMainHovered, setIsMainHovered] = useState(false);
  const [isMainFocused, setIsMainFocused] = useState(false);
  const [isMainActive, setIsMainActive] = useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(null);

  const [, drop] = useDrop({
    accept: [
      "DroppableDiv",
      "DroppableButton",
      "DroppableImage",
      "DroppableHeading",
      "DroppableParagraph",
      "DroppableIcon",
      "DroppableLink",
      "DroppableCheckbox",
      "DroppableRadio",
      "DroppableList",
      "DroppableTable",
      "DroppableVideo",
      "DroppableAudio",
      "DroppableTextInput",
      "DroppableRangeInput",
      "DroppablePasswordInput",
      "DroppableEmailInput",
      "DroppableNumberInput",
      "DroppableDateInput",
      "DroppableColorInput",
      "DroppableFileInput",
      "DroppableDropDown",
      "DroppableMap",
      "DroppableNav",
      "DroppableHeader",
      "DroppableHGroup",
      "DroppableFooter",
      "DroppableSection",
      "DroppableSpan",
      "DroppableForm",
      "DroppableArticle",
      "DroppableAside",
      "DroppableMain",
      "DroppableHorizontalRule",
      "DroppableLineBreak",
      "DroppableIframe",
    ],
    drop: (item, monitor) => {
      if (!monitor.didDrop()) {
        if (item.type === "DroppableDiv") {
          addDivision(main.id, item.name, item.type);
        } else if (
          item.type === "DroppableNav" ||
          item.type === "DroppableHeader" ||
          item.type === "DroppableHGroup" ||
          item.type === "DroppableFooter" ||
          item.type === "DroppableSection" ||
          item.type === "DroppableSpan" ||
          item.type === "DroppableForm" ||
          item.type === "DroppableArticle" ||
          item.type === "DroppableAside" ||
          item.type === "DroppableMain"
        ) {
          addCanvasDrop(main.id, item.name, item.type);
        } else {
          addElement(main.id, item.name, item.type);
        }
      }
    },
  });

  const constructGradientString = () => {
    const {
      backgroundGradientImage,
      gradientDirection,
      gradientShape,
      gradientPosition,
      startColor,
      endColor,
      gradientAngle,
      patternLength,
    } = main;

    switch (backgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${gradientDirection || "to right"}, ${
          startColor || "#ff7e5f"
        }, ${endColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${gradientShape || "circle"} at ${
          gradientPosition || "center"
        }, ${startColor || "#ff7e5f"}, ${endColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${gradientAngle || "45"}deg, ${
          startColor || "#606dbc"
        }, ${endColor || "#465298"} ${patternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${gradientShape || "circle"} at ${
          gradientPosition || "center"
        }, ${startColor || "#ff7e5f"}, ${endColor || "#feb47b"} ${
          patternLength || "10"
        }%)`;

      default:
        return "none";
    }
  };
  const constructHoverGradientString = () => {
    const {
      hoveredbackgroundGradientImage,
      hoveredgradientDirection,
      hoveredgradientShape,
      hoveredgradientPosition,
      hoveredstartColor,
      hoveredendColor,
      hoveredgradientAngle,
      hoveredpatternLength,
    } = main;

    switch (hoveredbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${hoveredgradientDirection || "to right"}, ${
          hoveredstartColor || "#ff7e5f"
        }, ${hoveredendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${hoveredgradientShape || "circle"} at ${
          hoveredgradientPosition || "center"
        }, ${hoveredstartColor || "#ff7e5f"}, ${hoveredendColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${hoveredgradientAngle || "45"}deg, ${
          hoveredstartColor || "#606dbc"
        }, ${hoveredendColor || "#465298"} ${hoveredpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          hoveredgradientShape || "circle"
        } at ${hoveredgradientPosition || "center"}, ${
          hoveredstartColor || "#ff7e5f"
        }, ${hoveredendColor || "#feb47b"} ${hoveredpatternLength || "10"}%)`;

      default:
        return "none";
    }
  };
  const constructFocusedGradientString = () => {
    const {
      focusedbackgroundGradientImage,
      focusedgradientDirection,
      focusedgradientShape,
      focusedgradientPosition,
      focusedstartColor,
      focusedendColor,
      focusedgradientAngle,
      focusedpatternLength,
    } = main;

    switch (focusedbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${focusedgradientDirection || "to right"}, ${
          focusedstartColor || "#ff7e5f"
        }, ${focusedendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${focusedgradientShape || "circle"} at ${
          focusedgradientPosition || "center"
        }, ${focusedstartColor || "#ff7e5f"}, ${focusedendColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${focusedgradientAngle || "45"}deg, ${
          focusedstartColor || "#606dbc"
        }, ${focusedendColor || "#465298"} ${focusedpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          focusedgradientShape || "circle"
        } at ${focusedgradientPosition || "center"}, ${
          focusedstartColor || "#ff7e5f"
        }, ${focusedendColor || "#feb47b"} ${focusedpatternLength || "10"}%)`;

      default:
        return "none";
    }
  };
  const constructActivatedGradientString = () => {
    const {
      activatedbackgroundGradientImage,
      activatedgradientDirection,
      activatedgradientShape,
      activatedgradientPosition,
      activatedstartColor,
      activatedendColor,
      activatedgradientAngle,
      activatedpatternLength,
    } = main;

    switch (activatedbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${activatedgradientDirection || "to right"}, ${
          activatedstartColor || "#ff7e5f"
        }, ${activatedendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${activatedgradientShape || "circle"} at ${
          activatedgradientPosition || "center"
        }, ${activatedstartColor || "#ff7e5f"}, ${
          activatedendColor || "#feb47b"
        })`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${
          activatedgradientAngle || "45"
        }deg, ${activatedstartColor || "#606dbc"}, ${
          activatedendColor || "#465298"
        } ${activatedpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          activatedgradientShape || "circle"
        } at ${activatedgradientPosition || "center"}, ${
          activatedstartColor || "#ff7e5f"
        }, ${activatedendColor || "#feb47b"} ${
          activatedpatternLength || "10"
        }%)`;

      default:
        return "none";
    }
  };

  const generateKeyframes = () => {
    if (!main.keyframes || main.keyframes.length === 0) return null;

    return main.keyframes.map((kf) => {
      const properties = {};
      let transform = "";

      Object.entries(kf.properties).forEach(([key, value]) => {
        // Remove 'KEY' prefix if present
        let processedKey = key.startsWith("KEY") ? key.slice(3) : key;

        // Handle transform properties
        if (
          processedKey === "scale" ||
          processedKey === "translateX" ||
          processedKey === "translateY" ||
          processedKey === "rotate" ||
          processedKey === "skewX" ||
          processedKey === "skewY"
        ) {
          if (value) {
            switch (processedKey) {
              case "scale":
                transform += `scale(${value}) `;
                break;
              case "translateX":
                transform += `translateX(${value}) `;
                break;
              case "translateY":
                transform += `translateY(${value}) `;
                break;
              case "rotate":
                transform += `rotate(${value}) `;
                break;
              case "skewX":
                transform += `skewX(${value}) `;
                break;
              case "skewY":
                transform += `skewY(${value}) `;
                break;
              default:
                break;
            }
          }
        } else {
          // Convert camelCase to kebab-case for other CSS properties
          properties[processedKey] = value;
        }
      });

      // Add transform property if it was built
      if (transform) {
        properties["transform"] = transform.trim();
      }

      return {
        offset: kf.position / 100,
        ...properties,
      };
    });
  };
  const combinedRef = useMemo(
    () => (node) => {
      mainRef.current = node;
      drop(node);
    },
    [drop]
  );
  useEffect(() => {
    if (mainRef.current && main.keyframes && main.keyframes.length > 0) {
      const keyframes = generateKeyframes();
      const animation = mainRef.current.animate(keyframes, {
        duration: parseFloat(main.animationDuration) * 1000 || 1000,
        delay: parseFloat(main.animationDelay) * 1000 || 0,
        iterations:
          main.animationIterationCount === "infinite"
            ? Infinity
            : parseFloat(main.animationIterationCount) || 1,
        direction: main.animationDirection || "normal",
        fill: main.animationFillMode || "none",
        easing: main.animationTimingFunction || "linear",
      });

      return () => animation.cancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    main.keyframes,
    main.animationDuration,
    main.animationDelay,
    main.animationIterationCount,
    main.animationDirection,
    main.animationFillMode,
    main.animationTimingFunction,
  ]);
  useEffect(() => {
    const fetchBackgroundImage = async () => {
      if (main.backgroundImage && main.backgroundImage.startsWith("./")) {
        const imageName = main.backgroundImage.replace("./", "");
        const file = files.find((f) => f.name === imageName);
        if (file && file.data) {
          setBackgroundImageUrl(file.data);
        } else {
          console.error("Image not found in files:", imageName);
        }
      } else {
        setBackgroundImageUrl(main.backgroundImage);
      }
    };

    fetchBackgroundImage();
  }, [main.backgroundImage, files]);

  const focusedStyles =
    isMainFocused && main.pseudoClass === "focus"
      ? {
          width: main.focusedwidth || main.width || "100%",
          height: main.focusedheight || main.height || "auto",

          padding: main.focusedpadding || main.padding || "8px",
          paddingTop: main.focusedpaddingTop || main.focusedpadding || "8px",
          paddingRight:
            main.focusedpaddingRight || main.focusedpadding || "8px",
          paddingBottom:
            main.focusedpaddingBottom || main.focusedpadding || "8px",
          paddingLeft: main.focusedpaddingLeft || main.focusedpadding || "8px",
          margin: main.focusedmargin || main.margin || "0px",
          marginTop: main.focusedmarginTop || main.focusedmargin || "0px",
          marginRight: main.focusedmarginRight || main.focusedmargin || "0px",
          marginBottom: main.focusedmarginBottom || main.focusedmargin || "0px",
          marginLeft: main.focusedmarginLeft || main.focusedmargin || "0px",

          borderTop:
            main.focusedborderTop ||
            main.focusedborder ||
            main.border ||
            "1px solid black",
          borderRight:
            main.focusedborderRight ||
            main.focusedborder ||
            main.border ||
            "1px solid black",
          borderBottom:
            main.focusedborderBottom ||
            main.focusedborder ||
            main.border ||
            "1px solid black",
          borderLeft:
            main.focusedborderLeft ||
            main.focusedborder ||
            main.border ||
            "1px solid black",
          borderSapcing:
            main.focusedborderSpacing || main.borderSpacing || "0px",
          borderCollapse:
            main.focusedborderCollapse || main.borderCollapse || "0px",
          borderRadius: main.focusedborderRadius || main.borderRadius || "0",
          outline: main.focusedoutline || main.outline || "none",

          position: main.focusedposition || main.position || "relative",
          top: main.focusedtop || main.top || "auto",
          right: main.focusedright || main.right || "auto",
          bottom: main.focusedbottom || main.bottom || "auto",
          left: main.focusedleft || main.left || "auto",
          zIndex: main.focusedzIndex || main.zIndex || "auto",
          boxShadow: main.focusedboxShadow || main.boxShadow || "none",
          opacity: main.focusedOpacity || main.opacity || 1,
          display: main.focuseddisplay || main.display || "block",
          flexDirection:
            main.focusedflexDirection || main.flexDirection || "column",
          flexWrap: main.focusedflexWrap || "nowrap",
          order: main.focusedorder || "0",
          flexGrow: main.focusedflexGrow || "0",
          flexShrink: main.focusedflexShrink || "1",
          flexBasis: main.focusedflexBasis || "auto",

          gridTemplateColumns: main.focusedgridTemplateColumns || "none",
          gridTemplateRows: main.focusedgridTemplateRows || "none",
          gridArea: main.focusedgridArea || "auto",
          gridAutoFlow: main.focusedgridAutoFlow || "row",
          gridAutoColumns: main.focusedgridAutoColumns || "auto",
          gridAutoRows: main.focusedgridAutoRows || "auto",
          columnGap: main.focusedcolumnGap || "0",
          rowGap: main.focusedrowGap || "0",

          justifyContent: main.focusedjustifyContent || "flex-start",
          alignItems: main.focusedalignItems || "stretch",
          alignContent: main.focusedalignContent || "stretch",
          alignSelf: main.focusedalignSelf || "auto",
          // placeContent: main.focusedplaceContent || "normal",
          // placeItems: main.focusedplaceItems || "normal",
          justifyItems: main.focusedjustifyItems || "stretch",
          justifySelf: main.focusedjustifySelf || "auto",

          gap: main.focusedgap || "0",
          visibility: main.focusedvisibility || "visible",

          color: main.focusedcolor || main.color || "#000000",
          backgroundColor:
            main.focusedbackgroundColor || main.backgroundColor || "#ffffff",
          backgroundImage: (() => {
            if (main.focusedbackgroundImage) {
              if (main.focusedbackgroundImage.startsWith("./")) {
                const imageName = main.focusedbackgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${main.focusedbackgroundImage})`;
            }
            if (main.backgroundImage) {
              if (main.backgroundImage.startsWith("./")) {
                const imageName = main.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${main.backgroundImage})`;
            }
            return (
              constructFocusedGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            main.focusedbackgroundPosition ||
            main.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            main.focusedbackgroundSize || main.backgroundSize || "auto",
          backgroundRepeat:
            main.focusedbackgroundRepeat || main.backgroundRepeat || "repeat",
          backgroundAttachment:
            main.focusedbackgroundAttachment ||
            main.backgroundAttachment ||
            "scroll",

          fontFamily: main.focusedfontFamily || main.fontFamily || "inherit",
          fontSize: main.focusedfontSize || main.fontSize || "inherit",
          fontWeight: main.focusedfontWeight || main.fontWeight || "normal",
          fontStyle: main.focusedfontStyle || main.fontStyle || "normal",
          fontVariant: main.focusedfontVariant || main.fontVariant || "normal",
          lineHeight: main.focusedlineHeight || main.lineHeight || "normal",
          textShadow: main.focusedtextShadow || main.textShadow || "none",
          textAlign: main.focusedtextAlign || main.textAlign || "left",
          verticalAlign:
            main.focusedverticalAlign || main.verticalAlign || "baseline",
          textDecoration:
            main.focusedtextDecoration || main.textDecoration || "none",
          textTransform:
            main.focusedtextTransform || main.textTransform || "none",
          letterSpacing:
            main.focusedletterSpacing || main.letterSpacing || "normal",
          wordSpacing: main.focusedwordSpacing || main.wordSpacing || "normal",
          textIndent: main.focusedtextIndent || main.textIndent || "0",
          whiteSpace: main.focusedwhiteSpace || main.whiteSpace || "normal",
          textOverflow: main.focusedtextOverflow || main.textOverflow || "clip",
          direction: main.focuseddirection || main.direction || "ltr",
          unicodeBidi: main.focusedunicodeBidi || main.unicodeBidi || "normal",
          writingMode:
            main.focusedwritingMode || main.writingMode || "horizontal-tb",

          cursor: main.focusedcursor || main.cursor || "default",
          filter: main.focusedfilter || main.filter || "none",
          backdropFilter:
            main.focusedbackdropFilter || main.backdropFilter || "none",
          mixBlendMode:
            main.focusedmixBlendMode || main.mixBlendMode || "normal",
          aspectRatio: main.focusedaspectRatio || main.aspectRatio || "auto",

          transform: main.focusedtransform || main.transform || "none",
          transition: main.focusedtransition || main.transition || "none",
        }
      : {};

  const hoverStyles =
    isMainHovered && main.pseudoClass === "hover"
      ? {
          width: main.hoveredwidth || main.width || "100%",
          height: main.hoveredheight || main.height || "auto",

          padding: main.hoveredpadding || main.padding || "8px",
          paddingTop: main.hoveredpaddingTop || main.hoveredpadding || "8px",
          paddingRight:
            main.hoveredpaddingRight || main.hoveredpadding || "8px",
          paddingBottom:
            main.hoveredpaddingBottom || main.hoveredpadding || "8px",
          paddingLeft: main.hoveredpaddingLeft || main.hoveredpadding || "8px",
          margin: main.hoveredmargin || main.margin || "0px",
          marginTop: main.hoveredmarginTop || main.hoveredmargin || "0px",
          marginRight: main.hoveredmarginRight || main.hoveredmargin || "0px",
          marginBottom: main.hoveredmarginBottom || main.hoveredmargin || "0px",
          marginLeft: main.hoveredmarginLeft || main.hoveredmargin || "0px",

          borderTop:
            main.hoveredborderTop ||
            main.hoveredborder ||
            main.border ||
            "1px solid black",
          borderRight:
            main.hoveredborderRight ||
            main.hoveredborder ||
            main.border ||
            "1px solid black",
          borderBottom:
            main.hoveredborderBottom ||
            main.hoveredborder ||
            main.border ||
            "1px solid black",
          borderLeft:
            main.hoveredborderLeft ||
            main.hoveredborder ||
            main.border ||
            "1px solid black",
          borderSapcing:
            main.hoveredborderSpacing || main.borderSpacing || "0px",
          borderCollapse:
            main.hoveredborderCollapse || main.borderCollapse || "0px",
          borderRadius: main.hoveredborderRadius || main.borderRadius || "0",
          outline: main.hoveredoutline || main.outline || "none",

          position: main.hoveredposition || main.position || "relative",
          top: main.hoveredtop || main.top || "auto",
          right: main.hoveredright || main.right || "auto",
          bottom: main.hoveredbottom || main.bottom || "auto",
          left: main.hoveredleft || main.left || "auto",
          zIndex: main.hoveredzIndex || main.zIndex || "auto",
          boxShadow: main.hoveredboxShadow || main.boxShadow || "none",
          opacity: main.hoveredOpacity || main.opacity || 1,
          display: main.hovereddisplay || main.display || "block",
          flexDirection:
            main.hoveredflexDirection || main.flexDirection || "column",
          flexWrap: main.hoveredflexWrap || "nowrap",
          order: main.hoveredorder || "0",
          flexGrow: main.hoveredflexGrow || "0",
          flexShrink: main.hoveredflexShrink || "1",
          flexBasis: main.hoveredflexBasis || "auto",

          gridTemplateColumns: main.hoveredgridTemplateColumns || "none",
          gridTemplateRows: main.hoveredgridTemplateRows || "none",
          gridArea: main.hoveredgridArea || "auto",
          gridAutoFlow: main.hoveredgridAutoFlow || "row",
          gridAutoColumns: main.hoveredgridAutoColumns || "auto",
          gridAutoRows: main.hoveredgridAutoRows || "auto",
          columnGap: main.hoveredcolumnGap || "0",
          rowGap: main.hoveredrowGap || "0",

          justifyContent: main.hoveredjustifyContent || "flex-start",
          alignItems: main.hoveredalignItems || "stretch",
          alignContent: main.hoveredalignContent || "stretch",
          alignSelf: main.hoveredalignSelf || "auto",
          // placeContent: main.hoveredplaceContent || "normal",
          // placeItems: main.hoveredplaceItems || "normal",
          justifyItems: main.hoveredjustifyItems || "stretch",
          justifySelf: main.hoveredjustifySelf || "auto",

          gap: main.hoveredgap || "0",
          visibility: main.hoveredvisibility || "visible",

          color: main.hoveredcolor || main.color || "#000000",
          backgroundColor:
            main.hoveredbackgroundColor || main.backgroundColor || "#ffffff",
          backgroundImage: (() => {
            if (main.hoveredbackgroundImage) {
              if (main.hoveredbackgroundImage.startsWith("./")) {
                const imageName = main.hoveredbackgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${main.hoveredbackgroundImage})`;
            }
            if (main.backgroundImage) {
              if (main.backgroundImage.startsWith("./")) {
                const imageName = main.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${main.backgroundImage})`;
            }
            return (
              constructHoverGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            main.hoveredbackgroundPosition ||
            main.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            main.hoveredbackgroundSize || main.backgroundSize || "auto",
          backgroundRepeat:
            main.hoveredbackgroundRepeat || main.backgroundRepeat || "repeat",
          backgroundAttachment:
            main.hoveredbackgroundAttachment ||
            main.backgroundAttachment ||
            "scroll",

          fontFamily: main.hoveredfontFamily || main.fontFamily || "inherit",
          fontSize: main.hoveredfontSize || main.fontSize || "inherit",
          fontWeight: main.hoveredfontWeight || main.fontWeight || "normal",
          fontStyle: main.hoveredfontStyle || main.fontStyle || "normal",
          fontVariant: main.hoveredfontVariant || main.fontVariant || "normal",
          lineHeight: main.hoveredlineHeight || main.lineHeight || "normal",
          textShadow: main.hoveredtextShadow || main.textShadow || "none",
          textAlign: main.hoveredtextAlign || main.textAlign || "left",
          verticalAlign:
            main.hoveredverticalAlign || main.verticalAlign || "baseline",
          textDecoration:
            main.hoveredtextDecoration || main.textDecoration || "none",
          textTransform:
            main.hoveredtextTransform || main.textTransform || "none",
          letterSpacing:
            main.hoveredletterSpacing || main.letterSpacing || "normal",
          wordSpacing: main.hoveredwordSpacing || main.wordSpacing || "normal",
          textIndent: main.hoveredtextIndent || main.textIndent || "0",
          whiteSpace: main.hoveredwhiteSpace || main.whiteSpace || "normal",
          textOverflow: main.hoveredtextOverflow || main.textOverflow || "clip",
          direction: main.hovereddirection || main.direction || "ltr",
          unicodeBidi: main.hoveredunicodeBidi || main.unicodeBidi || "normal",
          writingMode:
            main.hoveredwritingMode || main.writingMode || "horizontal-tb",

          cursor: main.hoveredcursor || main.cursor || "default",
          filter: main.hoveredfilter || main.filter || "none",
          backdropFilter:
            main.hoveredbackdropFilter || main.backdropFilter || "none",
          mixBlendMode:
            main.hoveredmixBlendMode || main.mixBlendMode || "normal",
          aspectRatio: main.hoveredaspectRatio || main.aspectRatio || "auto",

          transform: main.hoveredtransform || main.transform || "none",
          transition: main.hoveredtransition || main.transition || "none",
        }
      : {};

  const activeStyles =
    isMainActive && main.pseudoClass === "active"
      ? {
          width: main.activatedwidth || main.width || "100%",
          height: main.hoveredheight || main.height || "auto",

          padding: main.activatedpadding || main.padding || "8px",
          paddingTop:
            main.activatedpaddingTop || main.activatedpadding || "8px",
          paddingRight:
            main.activatedpaddingRight || main.activatedpadding || "8px",
          paddingBottom:
            main.activatedpaddingBottom || main.activatedpadding || "8px",
          paddingLeft:
            main.activatedpaddingLeft || main.activatedpadding || "8px",
          margin: main.activatedmargin || main.margin || "0px",
          marginTop: main.activatedmarginTop || main.activatedmargin || "0px",
          marginRight:
            main.activatedmarginRight || main.activatedmargin || "0px",
          marginBottom:
            main.activatedmarginBottom || main.activatedmargin || "0px",
          marginLeft: main.activatedmarginLeft || main.activatedmargin || "0px",

          borderTop:
            main.activatedborderTop ||
            main.activatedborder ||
            main.border ||
            "1px solid black",
          borderRight:
            main.activatedborderRight ||
            main.activatedborder ||
            main.border ||
            "1px solid black",
          borderBottom:
            main.activatedborderBottom ||
            main.activatedborder ||
            main.border ||
            "1px solid black",
          borderLeft:
            main.activatedborderLeft ||
            main.activatedborder ||
            main.border ||
            "1px solid black",
          borderSapcing:
            main.activatedborderSpacing || main.borderSpacing || "0px",
          borderCollapse:
            main.activatedborderCollapse || main.borderCollapse || "0px",
          borderRadius: main.activatedborderRadius || main.borderRadius || "0",
          outline: main.activatedoutline || main.outline || "none",

          position: main.activatedposition || main.position || "relative",
          top: main.activatedtop || main.top || "auto",
          right: main.activatedright || main.right || "auto",
          bottom: main.activatedbottom || main.bottom || "auto",
          left: main.activatedleft || main.left || "auto",
          zIndex: main.activatedzIndex || main.zIndex || "auto",

          boxShadow: main.activatedboxShadow || main.boxShadow || "none",
          opacity: main.activatedOpacity || main.opacity || 1,
          display: main.activateddisplay || main.display || "block",
          flexDirection:
            main.activatedflexDirection || main.flexDirection || "column",
          flexWrap: main.activatedflexWrap || "nowrap",
          order: main.activatedorder || "0",
          flexGrow: main.activatedflexGrow || "0",
          flexShrink: main.activatedflexShrink || "1",
          flexBasis: main.activatedflexBasis || "auto",

          gridTemplateColumns: main.activatedgridTemplateColumns || "none",
          gridTemplateRows: main.activatedgridTemplateRows || "none",
          gridArea: main.activatedgridArea || "auto",
          gridAutoFlow: main.activatedgridAutoFlow || "row",
          gridAutoColumns: main.activatedgridAutoColumns || "auto",
          gridAutoRows: main.activatedgridAutoRows || "auto",
          columnGap: main.activatedcolumnGap || "0",
          rowGap: main.activatedrowGap || "0",

          justifyContent: main.activatedjustifyContent || "flex-start",
          alignItems: main.activatedalignItems || "stretch",
          alignContent: main.activatedalignContent || "stretch",
          alignSelf: main.activatedalignSelf || "auto",
          // placeContent: main.activatedplaceContent || "normal",
          // placeItems: main.activatedplaceItems || "normal",
          justifyItems: main.activatedjustifyItems || "stretch",
          justifySelf: main.activatedjustifySelf || "auto",

          gap: main.activatedgap || "0",
          visibility: main.activatedvisibility || "visible",

          color: main.activatedcolor || main.color || "#000000",
          backgroundColor:
            main.activatedbackgroundColor || main.backgroundColor || "#ffffff",
          backgroundImage: (() => {
            if (main.activatedbackgroundImage) {
              if (main.activatedbackgroundImage.startsWith("./")) {
                const imageName = main.activatedbackgroundImage.replace(
                  "./",
                  ""
                );
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${main.activatedbackgroundImage})`;
            }
            if (main.backgroundImage) {
              if (main.backgroundImage.startsWith("./")) {
                const imageName = main.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${main.backgroundImage})`;
            }
            return (
              constructActivatedGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            main.activatedbackgroundPosition ||
            main.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            main.activatedbackgroundSize || main.backgroundSize || "auto",
          backgroundRepeat:
            main.activatedbackgroundRepeat || main.backgroundRepeat || "repeat",
          backgroundAttachment:
            main.activatedbackgroundAttachment ||
            main.backgroundAttachment ||
            "scroll",

          fontFamily: main.activatedfontFamily || main.fontFamily || "inherit",
          fontSize: main.activatedfontSize || main.fontSize || "inherit",
          fontWeight: main.activatedfontWeight || main.fontWeight || "normal",
          fontStyle: main.activatedfontStyle || main.fontStyle || "normal",
          fontVariant:
            main.activatedfontVariant || main.fontVariant || "normal",
          lineHeight: main.activatedlineHeight || main.lineHeight || "normal",
          textShadow: main.activatedtextShadow || main.textShadow || "none",
          textAlign: main.activatedtextAlign || main.textAlign || "left",
          verticalAlign:
            main.activatedverticalAlign || main.verticalAlign || "baseline",
          textDecoration:
            main.activatedtextDecoration || main.textDecoration || "none",
          textTransform:
            main.activatedtextTransform || main.textTransform || "none",
          letterSpacing:
            main.activatedletterSpacing || main.letterSpacing || "normal",
          wordSpacing:
            main.activatedwordSpacing || main.wordSpacing || "normal",
          textIndent: main.activatedtextIndent || main.textIndent || "0",
          whiteSpace: main.activatedwhiteSpace || main.whiteSpace || "normal",
          textOverflow:
            main.activatedtextOverflow || main.textOverflow || "clip",
          direction: main.activateddirection || main.direction || "ltr",
          unicodeBidi:
            main.activatedunicodeBidi || main.unicodeBidi || "normal",
          writingMode:
            main.activatedwritingMode || main.writingMode || "horizontal-tb",

          cursor: main.activatedcursor || main.cursor || "default",
          filter: main.activatedfilter || main.filter || "none",
          backdropFilter:
            main.activatedbackdropFilter || main.backdropFilter || "none",
          mixBlendMode:
            main.activatedmixBlendMode || main.mixBlendMode || "normal",
          aspectRatio: main.activatedaspectRatio || main.aspectRatio || "auto",

          transform: main.activatedtransform || main.transform || "none",
          transition: main.activatedtransition || main.transition || "none",
        }
      : {};

  const mainStyle = {
    width: main.width || "100%",
    height: main.height || "auto",
    padding: main.padding || "8px",
    paddingTop: main.paddingTop || main.padding || "8px",
    paddingRight: main.paddingRight || main.padding || "8px",
    paddingBottom: main.paddingBottom || main.padding || "8px",
    paddingLeft: main.paddingLeft || main.padding || "8px",
    margin: main.margin || "0px",
    marginTop: main.marginTop || main.margin || "0px",
    marginRight: main.marginRight || main.margin || "0px",
    marginBottom: main.marginBottom || main.margin || "0px",
    marginLeft: main.marginLeft || main.margin || "0px",

    borderTop: main.borderTop || main.border || "1px solid #000000",
    borderRight: main.borderRight || main.border || "1px solid #000000",
    borderBottom: main.borderBottom || main.border || "1px solid #000000",
    borderLeft: main.borderLeft || main.border || "1px solid #000000",

    borderSapcing: main.borderSpacing || "0px",
    borderCollapse: main.borderCollapse || "0px",
    borderRadius: main.borderRadius || "0",
    boxSizing: main.boxSizing || "border-box",

    position: main.position || "relative",
    top: main.top || "auto",
    right: main.right || "auto",
    bottom: main.bottom || "auto",
    left: main.left || "auto",
    zIndex: main.zIndex || "auto",

    display: main.display || "block",
    flexDirection: main.flexDirection || "column",
    flexWrap: main.flexWrap || "nowrap",
    order: main.order || "0",
    flexGrow: main.flexGrow || "0",
    flexShrink: main.flexShrink || "1",
    flexBasis: main.flexBasis || "auto",

    gridTemplateColumns: main.gridTemplateColumns || "none",
    gridTemplateRows: main.gridTemplateRows || "none",
    gridArea: main.gridArea || "auto",
    gridAutoFlow: main.gridAutoFlow || "row",
    gridAutoColumns: main.gridAutoColumns || "auto",
    gridAutoRows: main.gridAutoRows || "auto",
    columnGap: main.columnGap || "0",
    rowGap: main.rowGap || "0",

    justifyContent: main.justifyContent || "flex-start",
    alignItems: main.alignItems || "stretch",
    alignContent: main.alignContent || "stretch",
    alignSelf: main.alignSelf || "auto",
    // placeContent: main.placeContent || "normal",
    // placeItems: main.placeItems || "normal",
    justifyItems: main.justifyItems || "stretch",
    justifySelf: main.justifySelf || "auto",

    gap: main.gap || "0",
    visibility: main.visibility || "visible",

    color: main.color || "#000000",
    backgroundImage: backgroundImageUrl
      ? `url(${backgroundImageUrl})`
      : constructGradientString(),

    backgroundPosition: main.backgroundPosition || "0% 0%",
    backgroundSize: main.backgroundSize || "auto",
    backgroundRepeat: main.backgroundRepeat || "repeat",
    backgroundAttachment: main.backgroundAttachment || "scroll",
    boxShadow: main.boxShadow || "none",
    filter: main.filter || "none",
    backdropFilter: main.backdropFilter || "none",
    mixBlendMode: main.mixBlendMode || "normal",
    aspectRatio: main.aspectRatio || "auto",

    resize: main.resize || "none",
    userSelect: main.userSelect || "auto",
    overflow: main.resize && main.resize !== "none" ? "auto" : "visible",
    overflowX: main.overflowX || "hidden",
    overflowY: main.overflowY || "hidden",

    fontSize: main.fontSize || "inherit",
    fontFamily: main.fontFamily || "inherit",
    fontWeight: main.fontWeight || "normal",
    fontStyle: main.fontStyle || "normal",
    fontVariant: main.fontVariant || "normal",
    lineHeight: main.lineHeight || "normal",
    textAlign: main.textAlign || "left",
    textDecoration: main.textDecoration || "none",
    textTransform: main.textTransform || "none",
    textShadow: main.textShadow || "none",
    verticalAlign: main.verticalAlign || "baseline",
    letterSpacing: main.letterSpacing || "normal",
    wordSpacing: main.wordSpacing || "normal",
    textIndent: main.textIndent || "0",
    whiteSpace: main.whiteSpace || "normal",
    textOverflow: main.textOverflow || "clip",
    direction: main.direction || "ltr",
    unicodeBidi: main.unicodeBidi || "normal",
    writingMode: main.writingMode || "horizontal-tb",
    cursor: main.cursor || "default",
    outline: main.outline || "none",

    backgroundColor: isHovered ? "#ADD8E6" : main.backgroundColor || "#ffffff",

    listStyleType: main.listStyleType || "disc",
    listStyleImage: main.listStyleImage
      ? `url(${main.listStyleImage})`
      : "none",
    listStylePosition: main.listStylePosition || "outside",

    transform: main.transform || "none",
    transition:
      main.transition || "transform 0.3s ease, background-color 0.3s ease",

    ...hoverStyles,
    ...focusedStyles,
    ...activeStyles,
  };

  return (
    <div
      className="Main_Division"
      ref={combinedRef}
      style={mainStyle}
      onMouseEnter={() => setIsMainHovered(true)}
      onMouseLeave={() => setIsMainHovered(false)}
      onFocus={() => setIsMainFocused(true)}
      onBlur={() => setIsMainFocused(false)}
      onMouseDown={() => setIsMainActive(true)}
      onMouseUp={() => setIsMainActive(false)}
      tabIndex={0}
    >
      {main.children &&
        main.children.map((child) => {
          switch (child.name) {
            case "Button":
              return <ButtonComponent key={child.id} button={child} />;
            case "Image":
              return <ImageComponent key={child.id} image={child} />;
            case "Heading":
              return <HeaderComponent key={child.id} header={child} />;
            case "Paragraph":
              return <ParagraphComponent key={child.id} paragraph={child} />;
            case "Icon":
              return <IconComponent key={child.id} icon={child} />;
            case "Link":
              return <LinkComponent key={child.id} link={child} />;
            case "Checkbox":
              return <CheckboxComponent key={child.id} checkbox={child} />;
            case "Radio":
              return <RadioComponent key={child.id} radio={child} />;
            case "List":
              return <ListComponent key={child.id} list={child} />;
            case "Table":
              return <TableComponent key={child.id} table={child} />;
            case "Video":
              return <VideoComponent key={child.id} video={child} />;
            case "Text":
              return <TextInputComponent key={child.id} textInput={child} />;
            case "Range":
              return <RangeComponent key={child.id} range={child} />;
            case "Password":
              return (
                <PasswordInputComponent key={child.id} passwordInput={child} />
              );
            case "Email":
              return <EmailInputComponent key={child.id} emailInput={child} />;
            case "Number":
              return (
                <NumberInputComponent key={child.id} numberInput={child} />
              );
            case "Date":
              return <DateInputComponent key={child.id} dateInput={child} />;
            case "Color":
              return <ColorInputComponent key={child.id} colorInput={child} />;
            case "File":
              return <FileInputComponent key={child.id} fileInput={child} />;
            case "Audio":
              return <AudioComponent key={child.id} audio={child} />;
            case "DropDown":
              return <DropDownComponent key={child.id} dropdown={child} />;
            case "Output":
              return <OutputComponent key={child.id} output={child} />;
            case "Map":
              return <MapComponent key={child.id} map={child} />;
            case "Nav":
              return <Nav key={child.id} nav={child} />;
            case "Header":
              return <HeaderTag key={child.id} headertag={child} />;
            case "HGroup":
              return <Hgroup key={child.id} hgroup={child} />;
            case "Footer":
              return <Footer key={child.id} footer={child} />;
            case "Section":
              return <Section key={child.id} section={child} />;
            case "Span":
              return <Span key={child.id} span={child} />;
            case "Form":
              return <FormComponent key={child.id} form={child} />;
            case "Main":
              return <MainComponent key={child.id} main={child} />;
            case "Article":
              return <ArticleComponent key={child.id} article={child} />;
            case "Aside":
              return <AsideComponent key={child.id} aside={child} />;
            case "HorizontalRule":
              return <HRTag key={child.id} div={child} />;
            case "LineBreak":
              return <BRTag key={child.id} div={child} />;
            case "Iframe":
              return <IframeComponent key={child.id} div={child} />;
            default:
              return <Division key={child.id} div={child} />;
          }
        })}
    </div>
  );
};

export default MainComponent;
